export const API_URL =
  import.meta.env.VITE_API_URL ?? "http://localhost:4000/v1";
export const TOKEN_KEY = "duts_saas_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export class ApiError extends Error {
  status: number;
  code?: string;
  errors?: Record<string, string>;

  constructor(
    message: string,
    status: number,
    code?: string,
    errors?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.errors = errors;
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      message?: string;
      code?: string;
      errors?: Record<string, string>;
    } | null;
    const fieldMessage = body?.errors
      ? Object.values(body.errors).filter(Boolean).join(". ")
      : null;
    throw new ApiError(
      fieldMessage ||
        body?.message ||
        body?.error ||
        `Request failed (${response.status})`,
      response.status,
      body?.code,
      body?.errors,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export function exportUrl(
  businessId: string,
  file: "clients.csv" | "services.csv" | "bookings.csv",
): string {
  return `${API_URL}/businesses/${businessId}/exports/${file}`;
}

export async function downloadExport(
  businessId: string,
  file: "clients.csv" | "services.csv" | "bookings.csv",
): Promise<void> {
  const token = getToken();
  const response = await fetch(exportUrl(businessId, file), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new ApiError(`Export failed (${response.status})`, response.status);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = file;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function formatMoney(
  cents: number | null | undefined,
  currency = "USD",
): string {
  if (cents == null) return "Quote";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(cents / 100);
}
